import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  CAREERS_API_URL,
  CONTACT_EMAIL,
  EXPERIENCE,
  LANGUAGES,
  LOCATIONS,
  NOTICE_PERIODS,
  TEAMS,
  TURNSTILE_SITE_KEY,
} from "../constants";
import { submitCareers } from "../api";
import { usePageMeta } from "../usePageMeta";

// `turnstile` is already globally typed by Contact.tsx; only add this page's
// distinct onload callback so the two pages don't collide.
declare global {
  interface Window {
    quintaraCareersTurnstileReady?: () => void;
  }
}

// Mirror the original site's readiness gate: while either public value is still
// a placeholder, the form shows the email fallback instead of trying to send.
const API_READY =
  /^https:\/\/[A-Za-z0-9.-]+(:\d+)?\/([A-Za-z0-9._~-]+\/)*careers$/.test(CAREERS_API_URL) ||
  /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/careers$/.test(CAREERS_API_URL);
const KEY_READY = /^[A-Za-z0-9_-]{10,100}$/.test(TURNSTILE_SITE_KEY) && !TURNSTILE_SITE_KEY.includes("REPLACE");
const CONFIGURED = API_READY && KEY_READY;

const MAX_CV = 5 * 1024 * 1024;
const CV_EXT = ["pdf", "doc", "docx"];
const RISKY = /\.(php\d?|phtml|phar|exe|com|scr|msi|dll|bat|cmd|ps1|vbs|js|jar|sh|py|pl|cgi|aspx?|jsp|html?|svg|hta|lnk)\./i;

// Server field name -> element id on the page (insertion order drives which
// field gets focused first when several are invalid).
const FIELD_IDS: Record<string, string> = {
  name: "c-name",
  email: "c-email",
  phone: "c-phone",
  current_location: "c-city",
  area: "c-area",
  desired_role: "c-role",
  experience: "c-exp",
  preferred_location: "c-loc",
  linkedin: "c-linkedin",
  notice_period: "c-notice",
  languages: "c-langs",
  about: "c-about",
  cv: "c-cv",
  consent: "c-consent",
};
const FIELD_ORDER = Object.keys(FIELD_IDS);

const MAX_LEN: Record<string, number> = {
  name: 100,
  email: 254,
  phone: 30,
  current_location: 120,
  desired_role: 120,
  linkedin: 300,
  about: 5000,
};

const cvMessages: Record<string, string> = {
  cv_missing: "Please attach your CV.",
  cv_too_large: "Your CV is larger than 5 MB. Please upload a smaller file.",
  cv_empty: "That file is empty. Please choose your CV again.",
  cv_type: "Please upload a PDF or Word file (.pdf, .doc or .docx).",
  cv_name: "Please rename the file to something simple, like cv.pdf, and try again.",
  cv_unsafe:
    "We can't accept this file because it contains macros, scripts or embedded content. Please export a plain PDF and try again.",
  cv_invalid: "We couldn't read this file. Please export it again as a PDF and try again.",
};

const emailOk = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
const phoneOk = (v: string) => /^\+?[0-9 ().\-]{6,30}$/.test(v);
function linkedInOk(v: string) {
  try {
    const u = new URL(/^https?:\/\//i.test(v) ? v : "https://" + v);
    const h = u.hostname.toLowerCase();
    return (
      (u.protocol === "https:" || u.protocol === "http:") &&
      !u.username &&
      !u.password &&
      !u.port &&
      (h === "linkedin.com" || h.endsWith(".linkedin.com"))
    );
  } catch {
    return false;
  }
}

const fmtSize = (b: number) =>
  b >= 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB";

function cvProblem(file: File | null | undefined): string {
  if (!file) return cvMessages.cv_missing;
  const lower = file.name.toLowerCase();
  const ext = lower.includes(".") ? lower.split(".").pop()! : "";
  if (RISKY.test(lower)) return cvMessages.cv_name;
  if (CV_EXT.indexOf(ext) === -1) return cvMessages.cv_type;
  if (file.size === 0) return cvMessages.cv_empty;
  if (file.size > MAX_CV) return cvMessages.cv_too_large;
  return "";
}

function serverFieldMessage(key: string, code: string): string {
  if (key === "cv") return cvMessages[code] || cvMessages.cv_invalid;
  if (key === "consent") return "Please confirm you agree so we can consider your application.";
  if (key === "languages") return "Select at least one language.";
  if (code === "required") return "Required.";
  if (code === "too_long") return "This is too long.";
  if (key === "email") return "Enter a valid email, like name@company.com.";
  if (key === "phone") return "Enter a valid phone number, like +971 50 000 0000.";
  if (key === "linkedin") return "Enter your LinkedIn profile link, like https://linkedin.com/in/your-name.";
  return "Please check this field.";
}

type Status = { kind: "ok" | "bad"; content: ReactNode };

function CopyButton({ value }: { value: string }) {
  const [label, setLabel] = useState("Copy");
  async function copy() {
    const done = () => {
      setLabel("Copied");
      setTimeout(() => setLabel("Copy"), 1600);
    };
    try {
      await navigator.clipboard.writeText(value);
      done();
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — surface the text.
      setLabel("Select and copy");
    }
  }
  return (
    <button className="copy-btn" type="button" onClick={copy}>
      {label}
    </button>
  );
}

export function Careers() {
  usePageMeta(
    "Careers at Quintara",
    "Join Quintara and help bring business messaging on WhatsApp to the Middle East, Africa and beyond. Send us your CV.",
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [captchaError, setCaptchaError] = useState("");
  const [status, setStatus] = useState<Status | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitLabel, setSubmitLabel] = useState("Submit application");
  const [fileInfo, setFileInfo] = useState<string | null>(null);
  const [dragover, setDragover] = useState(false);

  const startedAt = useRef(Date.now());
  const cvInput = useRef<HTMLInputElement>(null);
  const captchaBox = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const rendered = useRef(false);

  // Load + render Turnstile once, on the careers page only (explicit render via
  // the script's onload callback). Guarded against the double-invoke that React
  // StrictMode does in dev, and against injecting the script twice.
  useEffect(() => {
    if (!CONFIGURED) return;

    const renderWidget = () => {
      if (rendered.current || !window.turnstile || !captchaBox.current) return;
      rendered.current = true;
      widgetId.current = window.turnstile.render(captchaBox.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action: "careers",
        theme: "light",
        size: captchaBox.current.clientWidth < 300 ? "compact" : "flexible",
        "refresh-expired": "auto",
        callback: () => setCaptchaError(""),
        "error-callback": () =>
          setCaptchaError("The security check couldn't load. Refresh the page, or email your CV instead."),
      });
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }
    window.quintaraCareersTurnstileReady = renderWidget;
    if (!document.getElementById("cf-turnstile-script")) {
      const s = document.createElement("script");
      s.id = "cf-turnstile-script";
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=quintaraCareersTurnstileReady";
      s.async = true;
      s.defer = true;
      s.onerror = () =>
        setCaptchaError(
          "The security check couldn't load. Check your connection or ad blocker, or email your CV instead.",
        );
      document.head.appendChild(s);
    }
  }, []);

  const captchaToken = () => {
    try {
      return widgetId.current !== null && window.turnstile ? window.turnstile.getResponse(widgetId.current) || "" : "";
    } catch {
      return "";
    }
  };
  const resetCaptcha = () => {
    try {
      if (widgetId.current !== null && window.turnstile) window.turnstile.reset(widgetId.current);
    } catch {
      /* ignore */
    }
  };

  const fail = (content: ReactNode) => {
    resetCaptcha(); // a Turnstile token is single-use — get a fresh one for the next attempt
    setStatus({ kind: "bad", content });
    setSending(false);
    setSubmitLabel("Submit application");
  };

  function showCv() {
    const file = cvInput.current?.files?.[0] ?? null;
    if (file) {
      setFileInfo(file.name + " · " + fmtSize(file.size));
      setErrors((prev) => ({ ...prev, cv: cvProblem(file) }));
    } else {
      setFileInfo(null);
    }
  }

  function focusField(key: string) {
    if (key === "languages") {
      document.querySelector<HTMLElement>('input[name="languages"]')?.focus();
      return;
    }
    document.getElementById(FIELD_IDS[key])?.focus();
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending) return; // ignore double clicks while uploading
    const form = e.currentTarget;
    const fd0 = new FormData(form);
    const get = (k: string) => String(fd0.get(k) ?? "").trim();
    const checkedLanguages = fd0.getAll("languages").map((v) => String(v));
    const file = cvInput.current?.files?.[0] ?? null;
    const consentChecked = fd0.get("consent") === "yes";

    // ----- Validation (convenience only; the server re-checks everything) -----
    const nextErrors: Record<string, string> = {};
    (
      ["name", "email", "phone", "current_location", "area", "desired_role", "experience", "preferred_location", "about"] as const
    ).forEach((k) => {
      if (!get(k)) nextErrors[k] = "Required.";
    });
    if (!nextErrors.email && !emailOk(get("email"))) nextErrors.email = "Enter a valid email, like name@company.com.";
    if (!nextErrors.phone && !phoneOk(get("phone")))
      nextErrors.phone = "Enter a valid phone number, like +971 50 000 0000.";
    if (get("linkedin") && !linkedInOk(get("linkedin")))
      nextErrors.linkedin = "Enter your LinkedIn profile link, like https://linkedin.com/in/your-name.";
    Object.keys(MAX_LEN).forEach((k) => {
      if (!nextErrors[k] && get(k).length > MAX_LEN[k])
        nextErrors[k] = "This is too long (" + MAX_LEN[k] + " characters max).";
    });
    if (checkedLanguages.length === 0) nextErrors.languages = "Select at least one language.";
    const cvErr = cvProblem(file);
    if (cvErr) nextErrors.cv = cvErr;
    if (!consentChecked) nextErrors.consent = "Please confirm you agree so we can consider your application.";

    setErrors(nextErrors);
    const firstBad = FIELD_ORDER.find((k) => nextErrors[k]);
    if (firstBad) {
      focusField(firstBad);
      return;
    }

    setStatus(null);

    if (!CONFIGURED) {
      setStatus({
        kind: "bad",
        content: (
          <>
            Online applications are not switched on yet. Email your CV to <b>{CONTACT_EMAIL}</b> and we will reply.
          </>
        ),
      });
      return;
    }

    const token = captchaToken();
    if (!token) {
      setCaptchaError("Please complete the security check above the Submit button.");
      return;
    }
    setCaptchaError("");

    const ext = file!.name.toLowerCase().split(".").pop();
    const fd = new FormData();
    (
      ["name", "email", "phone", "current_location", "area", "desired_role", "experience", "preferred_location", "notice_period", "about"] as const
    ).forEach((k) => fd.append(k, get(k)));
    const li = get("linkedin");
    fd.append("linkedin", li && !/^https?:\/\//i.test(li) ? "https://" + li : li);
    checkedLanguages.forEach((l) => fd.append("languages[]", l));
    fd.append("consent", consentChecked ? "yes" : "");
    fd.append("website", String(fd0.get("website") ?? ""));
    fd.append("elapsed_ms", String(Math.max(0, Math.round(Date.now() - startedAt.current))));
    fd.append("turnstile_token", token);
    fd.append("cv", file!, "cv." + ext); // the server names the attachment itself; the original name isn't needed

    setSending(true);
    setSubmitLabel("Uploading…");

    const result = await submitCareers(fd, (pct) => setSubmitLabel("Uploading… " + pct + "%"));

    if (result.ok) {
      const firstName = get("name").split(" ")[0];
      form.reset();
      setFileInfo(null);
      setDragover(false);
      setErrors({});
      resetCaptcha();
      setStatus({
        kind: "ok",
        content: (
          <>
            <b>Thanks, {firstName}.</b> Your application reached our team. If there's a fit, someone from the hiring team
            will contact you within three weeks.
          </>
        ),
      });
      setSent(true); // button stays disabled after a confirmed send
      setSubmitLabel("Application sent");
      return;
    }

    const code = result.error;
    if (code === "validation" && result.fields) {
      const fields = result.fields;
      const mapped: Record<string, string> = {};
      Object.keys(fields).forEach((k) => {
        if (FIELD_IDS[k]) mapped[k] = serverFieldMessage(k, fields[k]);
      });
      setErrors(mapped);
      fail(<>Please check the highlighted fields and try again.</>);
      const firstServerBad = FIELD_ORDER.find((k) => mapped[k]);
      if (firstServerBad) focusField(firstServerBad);
    } else if (code === "captcha_missing" || code === "captcha_invalid" || code === "captcha_expired") {
      setCaptchaError("The security check expired or didn't pass. Please complete it again.");
      fail(<>Please complete the security check again, then submit.</>);
    } else if (code === "too_fast") {
      fail(<>That was quicker than we expected. Please wait a few seconds and submit again.</>);
    } else if (code === "rate_limited") {
      fail(
        <>
          We've received several applications from your connection recently. Please try again later, or email your CV to{" "}
          <b>{CONTACT_EMAIL}</b>.
        </>,
      );
    } else if (code === "too_large") {
      setErrors((prev) => ({ ...prev, cv: cvMessages.cv_too_large }));
      fail(<>Your CV is too large. Please upload a file of 5 MB or less.</>);
    } else if (code === "timeout") {
      fail(
        <>
          The upload took too long. Please try again on a stronger connection, or email your CV to <b>{CONTACT_EMAIL}</b>
          .
        </>,
      );
    } else if (code === "network") {
      fail(
        <>
          We couldn't send your application. Check your connection and try again, or email your CV to{" "}
          <b>{CONTACT_EMAIL}</b>.
        </>,
      );
    } else {
      fail(
        <>
          We couldn't send your application just now. Please try again in a moment, or email your CV to{" "}
          <b>{CONTACT_EMAIL}</b>.
        </>,
      );
    }
  }

  const fieldClass = (id: string) => (errors[id] ? "field invalid" : "field");

  return (
    <>
      <section className="hero page-hero">
        <div className="hero-art" aria-hidden="true" style={{ opacity: 0.55 }}></div>
        <div className="wrap">
          <div className="hero-copy">
            <p className="eyebrow">Careers</p>
            <h1>Build the messaging channel a region runs on.</h1>
            <p className="lede">
              Quintara is building its founding team in Dubai and across our markets. If you know telecoms, AI, messaging
              or how businesses in the Middle East and Africa buy technology, we'd like to hear from you.
            </p>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="section-head">
            <p className="eyebrow">Where we're hiring</p>
            <h2>The teams we're putting together.</h2>
            <p className="lede">
              We don't list individual openings yet. Send a general application and tell us which team fits you best.
            </p>
          </div>
          <div className="segments">
            <div className="segment">
              <span className="tag">Product and engineering</span>
              <h3>Platform, AI and integrations</h3>
              <p>
                Messaging infrastructure on the WhatsApp Business Platform, Arabic-first conversational AI, and CRM and
                commerce connectors.
              </p>
            </div>
            <div className="segment">
              <span className="tag">Solutions and success</span>
              <h3>Regional hubs</h3>
              <p>
                Onboarding, WhatsApp Flows and template setup, quality management and customer success from hubs for the
                Middle East and North Africa, sub-Saharan Africa, and international markets.
              </p>
            </div>
            <div className="segment">
              <span className="tag">Commercial</span>
              <h3>Country and partner teams</h3>
              <p>Enterprise and government sales, agency partnerships and operator partnerships, led locally in each market.</p>
            </div>
            <div className="segment">
              <span className="tag">Central</span>
              <h3>Commercial management</h3>
              <p>
                Pricing, deal desk, revenue reporting, enablement and the relationship with Meta, plus finance, legal and
                operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }} id="apply">
        <div className="wrap form-grid">
          <form id="careers-form" noValidate onSubmit={onSubmit}>
            <h2 style={{ fontSize: "var(--step-2)" }}>Apply</h2>
            <div className="row2">
              <div className={fieldClass("name")}>
                <label htmlFor="c-name">Full name</label>
                <input id="c-name" name="name" autoComplete="name" required />
                <span className="error" aria-live="polite">{errors.name}</span>
              </div>
              <div className={fieldClass("email")}>
                <label htmlFor="c-email">Email</label>
                <input id="c-email" name="email" type="email" autoComplete="email" required />
                <span className="error" aria-live="polite">{errors.email}</span>
              </div>
            </div>
            <div className="row2">
              <div className={fieldClass("phone")}>
                <label htmlFor="c-phone">Phone</label>
                <input id="c-phone" name="phone" type="tel" autoComplete="tel" required placeholder="+971 50 000 0000" />
                <span className="error" aria-live="polite">{errors.phone}</span>
              </div>
              <div className={fieldClass("current_location")}>
                <label htmlFor="c-city">Current city and country</label>
                <input id="c-city" name="current_location" autoComplete="address-level2" required placeholder="Dubai, UAE" />
                <span className="error" aria-live="polite">{errors.current_location}</span>
              </div>
            </div>
            <div className="row2">
              <div className={fieldClass("area")}>
                <label htmlFor="c-area">Team you're applying for</label>
                <select id="c-area" name="area" required defaultValue="">
                  <option value="">Select a team</option>
                  {TEAMS.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
                <span className="error" aria-live="polite">{errors.area}</span>
              </div>
              <div className={fieldClass("desired_role")}>
                <label htmlFor="c-role">Role or job title you're looking for</label>
                <input id="c-role" name="desired_role" required placeholder="Solutions engineer" />
                <span className="error" aria-live="polite">{errors.desired_role}</span>
              </div>
            </div>
            <div className="row2">
              <div className={fieldClass("experience")}>
                <label htmlFor="c-exp">Years of experience</label>
                <select id="c-exp" name="experience" required defaultValue="">
                  <option value="">Select a range</option>
                  {EXPERIENCE.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
                <span className="error" aria-live="polite">{errors.experience}</span>
              </div>
              <div className={fieldClass("preferred_location")}>
                <label htmlFor="c-loc">Where you'd like to work</label>
                <select id="c-loc" name="preferred_location" required defaultValue="">
                  <option value="">Select a location</option>
                  {LOCATIONS.map((l) => (
                    <option key={l}>{l}</option>
                  ))}
                </select>
                <span className="error" aria-live="polite">{errors.preferred_location}</span>
              </div>
            </div>
            <div className="row2">
              <div className={fieldClass("linkedin")}>
                <label htmlFor="c-linkedin">
                  LinkedIn profile <span className="opt">(optional)</span>
                </label>
                <input id="c-linkedin" name="linkedin" type="url" inputMode="url" placeholder="https://linkedin.com/in/…" />
                <span className="error" aria-live="polite">{errors.linkedin}</span>
              </div>
              <div className="field">
                <label htmlFor="c-notice">
                  Notice period <span className="opt">(optional)</span>
                </label>
                <select id="c-notice" name="notice_period" defaultValue="">
                  <option value="">Select one</option>
                  {NOTICE_PERIODS.map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            <fieldset className={errors.languages ? "field checks-group invalid" : "field checks-group"} id="c-langs">
              <legend>Languages you work in</legend>
              <div className="check-row">
                {LANGUAGES.map((lang) => {
                  const inputId = "lang-" + lang.toLowerCase();
                  return (
                    <label className="check" htmlFor={inputId} key={lang}>
                      <input type="checkbox" id={inputId} name="languages" value={lang} />
                      <span>{lang}</span>
                    </label>
                  );
                })}
              </div>
              <span className="error" aria-live="polite">{errors.languages}</span>
            </fieldset>
            <div className={fieldClass("about")}>
              <label htmlFor="c-about">Tell us about yourself</label>
              <textarea
                id="c-about"
                name="about"
                required
                placeholder="What have you built, sold or run that's relevant to Quintara? What would you want to own here?"
              ></textarea>
              <span className="error" aria-live="polite">{errors.about}</span>
            </div>
            <div className={fieldClass("cv")}>
              <label htmlFor="c-cv">CV</label>
              <label
                className={"drop" + (fileInfo ? " has-file" : "") + (dragover ? " is-dragover" : "")}
                htmlFor="c-cv"
                id="c-drop"
                onDragEnter={() => setDragover(true)}
                onDragOver={() => setDragover(true)}
                onDragLeave={() => setDragover(false)}
                onDragEnd={() => setDragover(false)}
                onDrop={() => setDragover(false)}
              >
                <input
                  ref={cvInput}
                  id="c-cv"
                  name="cv"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  required
                  onChange={showCv}
                />
                <span className="drop-main" id="c-cv-name">
                  {fileInfo ?? (
                    <>
                      Drop your CV here or <u>choose a file</u>
                    </>
                  )}
                </span>
                <span className="drop-sub">PDF or Word, up to 5 MB</span>
              </label>
              <span className="error" aria-live="polite">{errors.cv}</span>
            </div>
            <div className="field">
              <label className="consent" htmlFor="c-consent">
                <input id="c-consent" name="consent" type="checkbox" value="yes" />
                <span>
                  I agree that Quintara may use my application and CV to assess me for current and future roles and keep
                  them for up to 12 months, as described in the <Link to="/privacy#applicants">privacy policy</Link>.
                </span>
              </label>
              <span className="error" id="c-consent-error" aria-live="polite">{errors.consent}</span>
            </div>
            {/* Honeypot: hidden from people, bots tend to fill it. The backend
                rejects any submission where "website" is non-empty. Pushed
                off-screen and out of tab order rather than display:none. */}
            <div
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", top: "auto", height: 0, width: 0, overflow: "hidden" }}
            >
              <label htmlFor="c-website">Website</label>
              <input id="c-website" name="website" type="text" tabIndex={-1} autoComplete="off" />
            </div>
            {/* Cloudflare Turnstile widget mounts here (careers page only). */}
            <div
              className={captchaError ? "field turnstile-field invalid" : "field turnstile-field"}
              id="c-captcha-field"
              hidden={!CONFIGURED}
            >
              <div ref={captchaBox} id="c-turnstile" />
              <span className="error" id="c-captcha-error" aria-live="polite">{captchaError}</span>
            </div>
            <div>
              <button className="btn btn-primary" type="submit" disabled={sending || sent}>
                {submitLabel}
              </button>
            </div>
            {status && (
              <div className={status.kind === "ok" ? "form-status ok" : "form-status bad"} role="status">
                {status.content}
              </div>
            )}
          </form>

          <aside className="contact-side">
            <dl>
              <div>
                <dt>What happens next</dt>
                <dd>
                  We read every application. If there's a fit, someone from the hiring team contacts you within three
                  weeks to arrange a first conversation.
                </dd>
              </div>
              <div>
                <dt>Equal opportunity</dt>
                <dd>
                  We hire on skills and experience. We don't ask for photos, age, marital status or nationality on this
                  form, and you don't need to include them in your CV.
                </dd>
              </div>
              <div>
                <dt>Recruitment agencies</dt>
                <dd>We don't accept unsolicited CVs from agencies.</dd>
              </div>
              <div>
                <dt>Questions</dt>
                <dd>
                  <span>{CONTACT_EMAIL}</span>
                  <CopyButton value={CONTACT_EMAIL} />
                </dd>
              </div>
            </dl>
          </aside>
        </div>
      </section>
    </>
  );
}
